/* eslint-disable no-undef */
import React, { useState } from "react";
import { CustomLink, CustomExternalLink, Modal } from "@common";
import {
  StyledAppBar,
  DesktopMenuContainer,
  DesktopMenuItem,
  MobileMenuContainer,
  MobileMenu,
  CTAButton,
  LoginButton,
  Logo,
  MobileMenuItem,
} from "./Navbar.styles";
import content from "@app/content/content-en.js";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@assets/icons/MenuIcon.svg";
import Container from "@mui/material/Container";
import SignUpPopUp from "./components/SignUpPopUp/SignUpPopUp";
import { trackEvent } from "../../services/analyticsService";
import { EVENTS, getStartedClickedProps, navbarButtonClickedProps } from "../Analytics/utils/events";

const navbarContent = content.navbar;
const pages = navbarContent.navbarLinks.links;
const buttons = navbarContent.navbarButtons;

const getButtonEvent = (text) => {
  switch (text) {
    case 'LOGIN': return EVENTS.LOGIN_CLICKED;
    case 'TEACHERS': return EVENTS.TEACHERS_CHECKOUT_CLICKED;
    case 'TEACH ON TATVA': return EVENTS.TEACHER_SITE_VISITED;
    default: return EVENTS.NAVBAR_BUTTON_CLICKED;
  }
};

function ResponsiveAppBar({ pathname }) {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [signupModalIsOpen, setSignupModalIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // The layout (and this navbar) persists across client-side navigation,
  // so derive this from the current route instead of checking once on mount.
  const isHomePage = pathname === "/" || pathname === "";

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const handleScroll = () => {
        setScrolled(window.scrollY > 62);
      };
      window.addEventListener("scroll", handleScroll);
      handleScroll();
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const isTransparent = isHomePage && !scrolled;

  return (
    <StyledAppBar position={"fixed"} transparent={isTransparent ? 1 : 0}>
      <Container maxWidth="false">
        <Toolbar disableGutters>
          <CustomLink to="/">
            <Logo src={navbarContent.logo.src} alt="" />
          </CustomLink>
          <MobileMenuContainer>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <img
                src={MenuIcon}
                alt=""
                style={{
                  width: "1.24675rem",
                  height: "1.03719rem",
                }}
              />
            </IconButton>
            <MobileMenu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
            >
              {pages
                .map((page) => (
                  <MobileMenuItem key={page} onClick={handleCloseNavMenu}>
                    <CustomLink
                      to={navbarContent.navbarLinks[page].url}
                      activeClassName="active"
                    >
                      {page}
                    </CustomLink>
                  </MobileMenuItem>
                ))}
              {buttons.map((link) => {
                if (link.endpoint) {
                  return (
                    <MobileMenuItem
                      key={link.text}
                      onClick={() => {
                        trackEvent(getButtonEvent(link.text), navbarButtonClickedProps({ buttonText: link.text, location: 'navbar_mobile' }));
                        handleCloseNavMenu();
                      }}
                    >
                      <CustomLink
                        to={link.endpoint}
                        activeClassName="active"
                      >
                        {link.text}
                      </CustomLink>
                    </MobileMenuItem>
                  );
                } else {
                  return (
                    <MobileMenuItem key={link.text}>
                      <CustomExternalLink
                        href={process.env.GATSBY_TEACHER_APP_URL}
                        target="_blank"
                        onClick={() => trackEvent(getButtonEvent(link.text), navbarButtonClickedProps({ buttonText: link.text, location: 'navbar_mobile' }))}
                      >
                        {link.text}
                      </CustomExternalLink>
                    </MobileMenuItem>
                  );
                }
              })}
              <MobileMenuItem>
                <CustomExternalLink
                  href={`${process.env.GATSBY_WEBAPP_BASE_URL || 'https://web.dev.tatva.app'}/dashboard`}
                  target="_blank"
                  onClick={() => {
                    trackEvent(EVENTS.GET_STARTED_CLICKED, getStartedClickedProps({ location: 'navbar_mobile' }));
                    handleCloseNavMenu();
                  }}
                >
                  Start Now
                </CustomExternalLink>
              </MobileMenuItem>
            </MobileMenu>
          </MobileMenuContainer>
          <DesktopMenuContainer>
            {pages
              .map((page) => (
                <DesktopMenuItem key={page} onClick={handleCloseNavMenu}>
                  <CustomLink
                    to={navbarContent.navbarLinks[page].url}
                    activeClassName="active"
                  >
                    {navbarContent.navbarLinks[page].text}
                  </CustomLink>
                </DesktopMenuItem>
              ))}
            {buttons.map((link) => {
              if (link.endpoint) {
                return (
                  <DesktopMenuItem
                    key={link.text}
                    onClick={() => {
                      trackEvent(getButtonEvent(link.text), navbarButtonClickedProps({ buttonText: link.text, location: 'navbar_desktop' }));
                      handleCloseNavMenu();
                    }}
                  >
                    <CustomLink
                      to={link.endpoint}
                      activeClassName="active"
                    >
                      {link.text}
                    </CustomLink>
                  </DesktopMenuItem>
                );
              } else {
                return (
                  <DesktopMenuItem key={link.text}>
                    <CustomExternalLink
                      href={process.env.GATSBY_TEACHER_APP_URL}
                      target="_blank"
                      onClick={() => trackEvent(getButtonEvent(link.text), navbarButtonClickedProps({ buttonText: link.text, location: 'navbar_desktop' }))}
                    >
                      {link.text}
                    </CustomExternalLink>
                  </DesktopMenuItem>
                );
              }
            })}
          </DesktopMenuContainer>
          <CTAButton
            onClick={() => {
              trackEvent(EVENTS.GET_STARTED_CLICKED, getStartedClickedProps({ location: 'navbar' }));
              window.open(process.env.GATSBY_WEBAPP_BASE_URL, '_blank');
            }}
          >
            Start Now
          </CTAButton>
        </Toolbar>
      </Container>

      <Modal isOpen={signupModalIsOpen} setIsOpen={setSignupModalIsOpen}>
        <SignUpPopUp setIsOpen={setSignupModalIsOpen} />
      </Modal>
    </StyledAppBar>
  );
}
export default ResponsiveAppBar;
